import React, { useEffect, useState } from 'react';
import { Container, Card, CardBody, CardHeader, Row, Col, Input, Button } from 'reactstrap';
import SnackbarContent from '@mui/material/SnackbarContent';
import LinearProgress from '@mui/material/LinearProgress';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import Report from './OrderComplainceDlvReport';
import { showNotification } from 'src/shared/components/Notification';
import { ERROR_MESSAGES, CLIENT_ADMIN } from 'src/utils/pnd/PndConstants';
import { IconButton, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const ComplianceDeliveryReportIndex = () => {
    const userType = localStorage.getItem("uri");
    const isAdmin = Number(userType) === CLIENT_ADMIN;

    const [loading, setLoading] = useState(false);
    const [isNoRecordFound, setNoRecordFound] = useState(false);
    const [isReport, setReports] = useState(null);
    const [exportReport, setExportReport] = useState(null);
    const [hubs, setHubs] = useState([]);
    const [riders, setRiders] = useState([]);

    // Filters
    const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedHub, setSelectedHub] = useState('');
    const [selectedRider, setSelectedRider] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [totalElements, setTotalElements] = useState(0);

    useEffect(() => {
        if (isAdmin) {
            fetchHubs();
        }
        fetchRiders('');
        generateComplianceDeliveryReport(0, pageSize);
    }, []);

    const fetchHubs = async () => {
        try {
            const response = await PndRestApi.GetAllActiveHub();
            if (response?.status === 200 && response?.data?.status === 200) {
                setHubs(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching hubs:', error);
        }
    };

    const fetchRiders = async (hubId) => {
        try {
            if (isAdmin) {
                if (hubId) {
                    const response = await PndRestApi.getAllRiderDetailsByHubId(hubId);
                    if (response?.status === 200 && response?.data?.status === 200) {
                        setRiders(response.data.data || []);
                    } else {
                        setRiders([]);
                    }
                } else {
                    const response = await PndRestApi.GetAllHubAssignedDriverDetails();
                    if (response?.status === 200 && response?.data?.status === 200) {
                        setRiders(response.data.data || []);
                    } else {
                        setRiders([]);
                    }
                }
            } else {
                const response = await PndRestApi.GetAllRidersDetails();
                if (response?.status === 200 && response?.data?.status === 200) {
                    setRiders(response.data.data || []);
                } else {
                    setRiders([]);
                }
            }
        } catch (error) {
            console.error('Error fetching riders:', error);
            setRiders([]);
        }
    };

    const handleHubChange = (e) => {
        const hubVal = e.target.value;
        setSelectedHub(hubVal);
        setSelectedRider('');
        fetchRiders(hubVal);
    };

    const generateComplianceDeliveryReport = async (page, size, overrides = null) => {
        try {
            setLoading(true);
            setNoRecordFound(false);
            setReports(null);

            const activeFromDate = overrides ? overrides.fromDate : fromDate;
            const activeToDate = overrides ? overrides.toDate : toDate;
            const activeHub = overrides ? overrides.selectedHub : selectedHub;
            const activeRider = overrides ? overrides.selectedRider : selectedRider;
            const activeStatus = overrides ? overrides.selectedStatus : selectedStatus;

            const payload = {
                fromDate: activeFromDate ? `${activeFromDate}T00:00:00.000Z` : null,
                toDate: activeToDate ? `${activeToDate}T23:59:59.000Z` : null,
                selectedHubId: isAdmin && activeHub ? Number(activeHub) : null,
                selectedRiderId: activeRider ? Number(activeRider) : null,
                orderStatusId: activeStatus ? Number(activeStatus) : null,
                page: page,
                size: size
            };
            const response = await PndRestApi.GenerateComplianceDeliveryReport(payload);
            if (response.status === 200 && response.data.status === 200) {
                const data = response.data.data;
                setReports(data.content || data);
                setTotalElements(data.totalElements || (data.content ? data.content.length : data.length));

                // Fetch full data for export in background
                fetchExportData(payload);
            } else if (response?.data?.status === 204) {
                setNoRecordFound(true);
            } else {
                showNotification("theme-light", "ltr", "Error", response.data.message || ERROR_MESSAGES.tryCatchError);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showNotification("theme-light", "ltr", "Error", ERROR_MESSAGES.tryCatchError);
        } finally {
            setLoading(false);
        }
    };

    const fetchExportData = async (payload) => {
        try {
            const exportPayload = { ...payload, page: 0, size: 5000 };
            const response = await PndRestApi.GenerateComplianceDeliveryReport(exportPayload);
            if (response.status === 200 && response.data.status === 200) {
                const data = response.data.data;
                setExportReport(data.content || data);
            }
        } catch (error) {
            console.error('Export fetch error:', error);
        }
    };

    const handleSearch = () => {
        setCurrentPage(0);
        generateComplianceDeliveryReport(0, pageSize);
    };

    const clearFilters = () => {
        const today = new Date().toISOString().split('T')[0];
        setFromDate(today);
        setToDate(today);
        setSelectedHub('');
        setSelectedRider('');
        setSelectedStatus('');
        setCurrentPage(0);
        fetchRiders('');
        generateComplianceDeliveryReport(0, pageSize, {
            fromDate: today,
            toDate: today,
            selectedHub: '',
            selectedRider: '',
            selectedStatus: ''
        });
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        generateComplianceDeliveryReport(newPage, pageSize);
    };

    const handleRowsPerPageChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(0);
        generateComplianceDeliveryReport(0, newSize);
    };

    return (
        <Container fluid>
            <Card className="border-0 shadow-sm mb-4">
                <CardHeader className="bg-white py-3">
                    <h5 className="mb-0 fw-bold text-primary">Compliance Delivery Report Filters</h5>
                </CardHeader>
                <CardBody>
                    <Row className="g-3 align-items-end">
                        <Col md={isAdmin ? 2 : 3}>
                            <label className="form-label small fw-bold text-muted mb-1">From Date</label>
                            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                        </Col>
                        <Col md={isAdmin ? 2 : 3}>
                            <label className="form-label small fw-bold text-muted mb-1">To Date</label>
                            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                        </Col>
                        {isAdmin && (
                            <Col md={2}>
                                <label className="form-label small fw-bold text-muted mb-1">Hub</label>
                                <Input type="select" value={selectedHub} onChange={handleHubChange}>
                                    <option value="">All Hubs</option>
                                    {hubs.map((hub) => (
                                        <option key={hub.hubId} value={hub.hubId}>
                                            {hub.hubName}
                                        </option>
                                    ))}
                                </Input>
                            </Col>
                        )}
                        <Col md={2}>
                            <label className="form-label small fw-bold text-muted mb-1">Rider</label>
                            <Input type="select" value={selectedRider} onChange={(e) => setSelectedRider(e.target.value)}>
                                <option value="">All Riders</option>
                                {riders.map((rider) => (
                                    <option key={rider.riderId} value={rider.riderId}>
                                        {rider.riderName || rider.driverName}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={2}>
                            <label className="form-label small fw-bold text-muted mb-1">Status</label>
                            <Input type="select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                                <option value="">All Status</option>
                                <option value="3">Full Delivered</option>
                                <option value="9">Partially Delivered</option>
                                <option value="5">Undelivered</option>
                            </Input>
                        </Col>
                        <Col md={2} className="d-flex gap-2">
                            <Tooltip title="Clear Filters" arrow>
                                <IconButton
                                    onClick={clearFilters}
                                    sx={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        '&:hover': { backgroundColor: '#e2e6ea' }
                                    }}
                                >
                                    <RestartAltIcon color="action" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Search Report" arrow>
                                <IconButton
                                    color="primary"
                                    onClick={handleSearch}
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        '&:hover': { backgroundColor: 'primary.dark' }
                                    }}
                                >
                                    <SearchIcon />
                                </IconButton>
                            </Tooltip>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {loading && <LinearProgress color="primary" sx={{ mb: 2 }} />}

            {isReport && (
                <Report
                    isReport={isReport}
                    exportReport={exportReport}
                    page={currentPage}
                    rowsPerPage={pageSize}
                    totalElements={totalElements}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            )}

            {isNoRecordFound && <SnackbarContent message={"No Record Found"} sx={{ mt: 2 }} />}
        </Container>
    );
};

export default ComplianceDeliveryReportIndex;